// Pure classification of the raw text /open/error is given (see
// route-open.js / open-error.js). Unlike /auth/error (login-error-copy.js),
// which gets a reason code the auth service already mapped, this page is the
// catch-all for anything that goes wrong BEFORE a connection is even
// reached -- an unknown or deactivated provider (rapid-api-auth,
// api/ddd/service/auth-provider/service.go Auth, GetProvider ->
// gorm.ErrRecordNotFound), an unredeemed passive-login token
// (ErrPassiveLoginNotRedeemed), or any other internal error: util.go's
// handleErr sends err.Error() verbatim, base64-encoded, as ?error=. There is
// no reason code to switch on, so this pattern-matches the decoded text --
// best-effort, and deliberately narrow. A wrong "your sign-in link expired"
// or "this needs your administrator" claim is worse than a plain one, so
// anything that doesn't match a known shape gets the generic case rather
// than being folded into a specific one.
//
// credential: the identity provider rejected EducationXR's OWN client
// credential (secret or certificate) -- an administrator has to fix this,
// the person can't. Seen as `provider driver: token endpoint reports
// invalid_client` (provider-driver/driver.go, oidc/driver.go) wrapping
// OAuth2's own `invalid_client` / `unauthorized_client`, or Entra's
// AADSTS700027 ("no matching credential") and AADSTS7000215 ("invalid
// client secret").
const CREDENTIAL_PATTERNS = [
  'invalid_client',
  'unauthorized_client',
  'aadsts700027',
  'aadsts7000215',
  'invalid certificate',
  'invalid secret'
]

// expired: the sign-in itself (a link, a token, a stored session) is the
// thing that's stale or gone missing -- seen as gorm's "record not found"
// (a deactivated/unknown provider), ErrPassiveLoginNotRedeemed's "not
// redeemed", or a mismatched nonce/state. This is the one case where
// "expired, or the connection was turned off" is an accurate guess.
const EXPIRED_PATTERNS = [
  'expired',
  'not redeemed',
  'not_redeemed',
  'record not found',
  'invalid state',
  'state mismatch',
  'nonce mismatch'
]

const matchesAny = (haystack, needles) => needles.some(needle => haystack.includes(needle))

// The copy for /open/error. `key` says which row was used, for tests. Never
// includes the raw `message` -- callers show that separately, behind the
// technical-details disclosure.
export const classifyOpenError = message => {
  const text = (message || '').toLowerCase()

  if (matchesAny(text, CREDENTIAL_PATTERNS)) {
    return {
      key: 'credential',
      title: "This sign-in isn't set up correctly",
      body: "Your organization's single sign-on connection to EducationXR needs attention. Your administrator can fix it; in the meantime you can sign in with your EducationXR password."
    }
  }

  if (message && matchesAny(text, EXPIRED_PATTERNS)) {
    return {
      key: 'expired',
      title: "We couldn't start signing you in",
      body: "The sign-in link didn't work. It may have expired, or the connection may have been turned off. Start again from the sign-in page, or sign in with your password."
    }
  }

  return {
    key: 'generic',
    title: "We couldn't start signing you in",
    body: 'Something went wrong before your sign-in could finish. Start again from the sign-in page, or sign in with your password.'
  }
}
